import { AuditEndpoint, AuditRecentVisit, AuditUserActivity } from '../models';
import * as St from '../styles';
import { formatNumber } from '../utils';
import CardStatus, { CardStatusProps } from './CardStatus';

interface TableCardProps extends CardStatusProps {
  title: string;
  subtitle: string;
  badge: string;
  maxHeight: number;
  minWidth?: number;
  children: React.ReactNode;
}

const TableCard: React.FC<TableCardProps> = ({
  title,
  subtitle,
  badge,
  maxHeight,
  minWidth,
  loading,
  error,
  children,
}) => (
  <St.TableCard>
    <St.CardHeader>
      <St.CardTitle>
        <h3>{title}</h3>
        <span>{subtitle}</span>
      </St.CardTitle>
      <St.Badge>{badge}</St.Badge>
    </St.CardHeader>
    <CardStatus loading={loading} error={error} height={maxHeight}>
      <St.ScrollList maxHeight={maxHeight}>
        <St.Table minWidth={minWidth}>{children}</St.Table>
      </St.ScrollList>
    </CardStatus>
  </St.TableCard>
);

export const EndpointsTable: React.FC<
  { endpoints?: AuditEndpoint[] } & CardStatusProps
> = ({ endpoints = [], loading, error }) => (
  <TableCard
    loading={loading}
    error={error}
    title="Endpoints de API"
    subtitle="Requisições vinculadas às visitas selecionadas"
    badge="TOP 30"
    maxHeight={330}
  >
    <thead>
      <tr>
        <th>ENDPOINT / SERVIÇO</th>
        <th>MÉTODO</th>
        <th className="center">CHAMADAS DE API</th>
      </tr>
    </thead>
    <tbody>
      {endpoints.length === 0 && (
        <tr>
          <td colSpan={3}>Nenhuma chamada no período.</td>
        </tr>
      )}
      {endpoints.map((item) => (
        <tr key={`${item.metodo} ${item.endpoint}`}>
          <td>
            {item.endpoint}
            <small>{item.servico}</small>
          </td>
          <td>{item.metodo}</td>
          <td className="center">{formatNumber(item.chamadas)}</td>
        </tr>
      ))}
    </tbody>
  </TableCard>
);

interface UserActivityTableProps extends CardStatusProps {
  users?: AuditUserActivity[];
  /** Id do filtro de usuário aplicado; a linha fica destacada. */
  selected?: string;
  onSelect: (idUsuario: string) => void;
}

export const UserActivityTable: React.FC<UserActivityTableProps> = ({
  users = [],
  selected,
  loading,
  error,
  onSelect,
}) => (
  <TableCard
    loading={loading}
    error={error}
    title="Atividade por usuário"
    subtitle="Selecione um registro para filtrar"
    badge="TOP 50"
    maxHeight={330}
  >
    <thead>
      <tr>
        <th>USUÁRIO REGISTRADO</th>
        <th className="center">VISITAS</th>
        <th className="center">CHAMADAS DE API</th>
        <th className="center">SERVIÇOS</th>
      </tr>
    </thead>
    <tbody>
      {users.length === 0 && (
        <tr>
          <td colSpan={4}>Nenhum usuário no período.</td>
        </tr>
      )}
      {users.map((item) => (
        <St.ClickableRow
          key={item.idUsuario}
          tabIndex={0}
          selected={item.idUsuario === selected}
          aria-selected={item.idUsuario === selected}
          title={
            item.idUsuario === selected
              ? 'Remover filtro deste usuário'
              : 'Filtrar por este usuário'
          }
          onClick={() => onSelect(item.idUsuario)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(item.idUsuario);
            }
          }}
        >
          <td>
            {item.usuario}
            <small>{item.idUsuario}</small>
          </td>
          <td className="center">{formatNumber(item.visitas)}</td>
          <td className="center">{formatNumber(item.chamadas)}</td>
          <td className="center">{item.servicos}</td>
        </St.ClickableRow>
      ))}
    </tbody>
  </TableCard>
);

// Até 100 visitas, as mais recentes primeiro (o contrato não pagina).
interface RecentVisitsTableProps extends CardStatusProps {
  visits?: AuditRecentVisit[];
  /** correlationId da visita aberta no modal. */
  selected?: string;
  onSelect: (visit: AuditRecentVisit) => void;
}

export const RecentVisitsTable: React.FC<RecentVisitsTableProps> = ({
  visits = [],
  selected,
  loading,
  error,
  onSelect,
}) => (
  <TableCard
    loading={loading}
    error={error}
    title="Visitas recentes"
    subtitle="Selecione uma visita para ver as chamadas de API associadas"
    badge="ATÉ 100"
    maxHeight={330}
    minWidth={1000}
  >
    <thead>
      <tr>
        <th>DATA E HORA</th>
        <th>USUÁRIO / ID</th>
        <th>TELA / ROTA DO FRONT-END</th>
        <th>PRIMEIRA CHAMADA DE API</th>
      </tr>
    </thead>
    <tbody>
      {visits.length === 0 && (
        <tr>
          <td colSpan={4}>Nenhuma visita no período.</td>
        </tr>
      )}
      {visits.map((item) => (
        <St.ClickableRow
          key={item.correlationId}
          tabIndex={0}
          selected={item.correlationId === selected}
          title="Ver chamadas de API desta visita"
          onClick={() => onSelect(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(item);
            }
          }}
        >
          <td>{item.dataHora}</td>
          <td>
            {item.usuario}
            <small>{item.idUsuario}</small>
          </td>
          <td>
            {item.tela}
            <small>{item.rota}</small>
          </td>
          <td className="url">
            {item.apiPath}
            <small>
              {formatNumber(item.chamadas)}{' '}
              {item.chamadas === 1 ? 'chamada' : 'chamadas'} na visita
            </small>
          </td>
        </St.ClickableRow>
      ))}
    </tbody>
  </TableCard>
);
